/**
 * T3044 · Pre-init acquire buffer — 4096 entries with priority-based overflow.
 *
 * Spec anchor: file 05 §L2930.
 *
 * The registry accepts `acquire*` calls before init completes (deep-link
 * cold start can request >50 screens before NATS CONNECT lands). Each
 * pending acquisition is tagged with a priority:
 *
 *   - 'screen' — wrapped via <SdkScreen>; user is looking at the row right now.
 *   - 'shell'  — layout-mounted hook; can be safely starved.
 *
 * On overflow (4096 entries):
 *   - drop the oldest 'shell' entry first;
 *   - if no shells remain, drop the oldest 'screen' (NOT drop-newest — newest
 *     is what the user just navigated to);
 *   - emit `acquire:dropped` with `{ reason: 'PRE_INIT_BUFFER_FULL' }` so the
 *     dropped hook's React effect surfaces SdkError('PRE_INIT_BUFFER_FULL')
 *     on `query.error` and re-triggers on next render.
 *
 * On first successful CONNECT ack, `drainOnConnect()` replays every retained
 * entry in order.
 */

/** Sample emitter shape — kept narrow so tests can mock cleanly. */
export interface AcquireEmitter {
  emit(event: 'acquire:dropped', payload: { reason: string }): void;
}

export interface PendingAcq {
  readonly priority: 'screen' | 'shell';
  readonly replay: () => void;
  readonly createdAt: number;
}

/** Hard-coded by spec — see file 05 §L2930. */
export const PRE_INIT_BUFFER_MAX = 4096;

/** SdkError code surfaced on dropped acquisitions. */
export const PRE_INIT_BUFFER_FULL_REASON = 'PRE_INIT_BUFFER_FULL' as const;

export interface PreInitBuffer {
  /** Enqueue one pending acquisition (overflows are handled in-line). */
  bufferAcquire(entry: PendingAcq): void;
  /** Replay every retained entry on first CONNECT ack. */
  drainOnConnect(): void;
  /** Test introspection — current size. */
  size(): number;
  /** Test introspection — current contents (frozen snapshot). */
  snapshot(): readonly PendingAcq[];
}

export function createPreInitBuffer(emitter: AcquireEmitter): PreInitBuffer {
  const buffer: PendingAcq[] = [];

  function bufferAcquire(entry: PendingAcq): void {
    if (buffer.length >= PRE_INIT_BUFFER_MAX) {
      // Drop the oldest 'shell' first.
      const shellIdx = buffer.findIndex((e) => e.priority === 'shell');
      if (shellIdx !== -1) {
        buffer.splice(shellIdx, 1);
      } else {
        // No shells left — drop the oldest screen (NOT newest).
        buffer.shift();
      }
      emitter.emit('acquire:dropped', { reason: PRE_INIT_BUFFER_FULL_REASON });
    }
    buffer.push(entry);
  }

  function drainOnConnect(): void {
    // Drain in order — first-come first-replayed.
    while (buffer.length > 0) {
      const next = buffer.shift();
      next?.replay();
    }
  }

  function size(): number {
    return buffer.length;
  }

  function snapshot(): readonly PendingAcq[] {
    return Object.freeze(buffer.slice());
  }

  return { bufferAcquire, drainOnConnect, size, snapshot };
}
